/**
 * Pixel flag helpers: 16x11 pixels, each a palette index "0".."7" (row-major string).
 * Used by the catalog (pre-made flags), the flag editor and the renderers.
 */
import { FLAG_H, FLAG_MAX_COLORS, FLAG_W } from "./schema";

export const FLAG_LAYOUTS = ["tricolorV", "tricolorH", "cross", "saltire", "canton", "diagonal", "sun", "chevron", "quarters", "stripes"] as const;
export type FlagLayout = (typeof FLAG_LAYOUTS)[number];

/** Pixel string of a layout using palette indices 0, 1, 2. */
export function flagPixels(layout: FlagLayout): string {
  let out = "";
  for (let y = 0; y < FLAG_H; y++) {
    for (let x = 0; x < FLAG_W; x++) out += String(flagIndexAt(layout, x, y));
  }
  return out;
}

function flagIndexAt(layout: FlagLayout, x: number, y: number): number {
  switch (layout) {
    case "tricolorV":
      return x < 5 ? 0 : x < 11 ? 1 : 2;
    case "tricolorH":
      return y < 4 ? 0 : y < 7 ? 1 : 2;
    case "cross":
      return x === 5 || x === 6 || y === 5 ? 1 : 0;
    case "saltire": {
      const a = Math.abs(x * 11 - y * 16) <= 12;
      const b = Math.abs(x * 11 - (10 - y) * 16) <= 12;
      return a || b ? 1 : 0;
    }
    case "canton":
      return x < 7 && y < 6 ? (x === 3 && y === 2 ? 2 : 1) : y % 2 === 0 ? 0 : 2;
    case "diagonal":
      return x * 11 + y * 16 < 176 ? 0 : 1;
    case "sun": {
      const dx = x - 7.5;
      const dy = y - 5;
      const d2 = dx * dx + dy * dy;
      return d2 <= 6.5 ? 2 : d2 <= 12 && (x + y) % 2 === 0 ? 1 : 0;
    }
    case "chevron":
      return x <= 5 - Math.abs(y - 5) + 1 ? 1 : y < 5 ? 0 : 2;
    case "quarters":
      return (x < 8) === (y < 5) ? 0 : 1;
    case "stripes":
      return y % 2 === 0 ? 0 : 1;
  }
}

/** True when `pixels` is a valid 16x11 string for a palette of `colors` entries. */
export function validFlagPixels(pixels: unknown, colors: number): pixels is string {
  if (typeof pixels !== "string" || pixels.length !== FLAG_W * FLAG_H) return false;
  if (colors < 2 || colors > FLAG_MAX_COLORS) return false;
  for (let i = 0; i < pixels.length; i++) {
    const c = pixels.charCodeAt(i) - 48;
    if (c < 0 || c >= colors) return false;
  }
  return true;
}

/** Palette index at (x, y). */
export function flagIndex(pixels: string, x: number, y: number): number {
  return pixels.charCodeAt(y * FLAG_W + x) - 48;
}

/** Copy of `pixels` with (x, y) set to `index`. */
export function setFlagPixel(pixels: string, x: number, y: number, index: number): string {
  if (x < 0 || y < 0 || x >= FLAG_W || y >= FLAG_H) return pixels;
  const i = y * FLAG_W + x;
  return pixels.slice(0, i) + String(index) + pixels.slice(i + 1);
}
